'use client'

import Link from 'next/link'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { containerClassName } from '@/components/Container'
import {
  getRoomTypeCardProps,
  RoomTypeCard,
} from '@/components/RoomTypes/RoomTypeCard'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import type { ApartmentAvailabilityItem } from '@/lib/apartments/apartmentsQuery'
import { groupApartmentsByRoomType } from '@/lib/apartments/apartmentsQuery'
import type { RoomTypeId } from '@/lib/apartments/roomTypeOptions'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

import { getLocationRoomTypesCarouselLayout } from './locationRoomTypesCarouselLayout'

const carouselControlClassName =
  'static top-auto size-10 translate-none rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

const desktopScrollMediaQuery = '(min-width: 64rem)'

function isDesktopCarouselScroll(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(desktopScrollMediaQuery).matches
  )
}

type LocationRoomTypesCarouselSectionProps = {
  apartments: ApartmentAvailabilityItem[]
  excludeRoomType?: RoomTypeId
  hasPromotion?: boolean
}

export function LocationRoomTypesCarouselSection({
  apartments,
  excludeRoomType,
  hasPromotion = false,
}: LocationRoomTypesCarouselSectionProps) {
  const locale = useLocale()
  const messages = useMessages()
  const copy = messages.location.roomTypesCarousel
  const gridCopy = messages.roomTypes.grid
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [canScrollCarousel, setCanScrollCarousel] = useState(false)

  const listings = useMemo(
    () => groupApartmentsByRoomType(apartments, { excludeRoomType }),
    [apartments, excludeRoomType],
  )

  const layout = useMemo(
    () => getLocationRoomTypesCarouselLayout(listings.length),
    [listings.length],
  )

  useEffect(() => {
    if (!carouselApi) return

    const updateCanScroll = () => {
      setCanScrollCarousel(carouselApi.canScrollNext() || carouselApi.canScrollPrev())
    }

    updateCanScroll()
    carouselApi.on('reInit', updateCanScroll)
    carouselApi.on('select', updateCanScroll)
    carouselApi.on('resize', updateCanScroll)

    return () => {
      carouselApi.off('reInit', updateCanScroll)
      carouselApi.off('select', updateCanScroll)
      carouselApi.off('resize', updateCanScroll)
    }
  }, [carouselApi, listings.length])

  const handleCarouselNext = useCallback(() => {
    if (!carouselApi) {
      return
    }

    const currentSnap = carouselApi.selectedScrollSnap()
    const lastSnap = carouselApi.scrollSnapList().length - 1

    if (isDesktopCarouselScroll() && currentSnap === 0 && lastSnap > 0) {
      carouselApi.scrollTo(lastSnap)
      return
    }

    carouselApi.scrollNext()
  }, [carouselApi])

  const handleCarouselPrev = useCallback(() => {
    if (!carouselApi) {
      return
    }

    const currentSnap = carouselApi.selectedScrollSnap()
    const lastSnap = carouselApi.scrollSnapList().length - 1

    if (isDesktopCarouselScroll() && currentSnap === lastSnap && lastSnap > 0) {
      carouselApi.scrollTo(0)
      return
    }

    carouselApi.scrollPrev()
  }, [carouselApi])

  if (listings.length === 0) {
    return null
  }

  return (
    <section
      aria-labelledby="location-room-types-carousel-heading"
      className="overflow-x-clip bg-cream"
    >
      <Carousel
        aria-label={copy.carouselAriaLabel}
        className="w-full"
        opts={{ align: 'start', containScroll: 'trimSnaps' }}
        setApi={setCarouselApi}
      >
        <div className={cn(containerClassName, 'pt-8 pb-8 lg:pt-20')}>
          <div className="flex flex-col md:gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
            {/* Avoid cn()/twMerge on the heading — it strips the custom `text-title` class against `text-*` utilities */}
            <ColorTrailText
              as="h2"
              className="max-w-[37.5rem] text-balance text-title font-semibold leading-none tracking-[-0.04em] text-bluetext"
              id="location-room-types-carousel-heading"
              segments={[
                {
                  finalColor: 'var(--brand-bluetext)',
                  text: copy.titleLine1,
                  trail: false,
                },
                {
                  finalColor: 'var(--brand-dark-green)',
                  text: copy.titleAccent,
                },
              ]}
            />

            <div className="flex w-full max-w-[32.5rem] flex-col items-start gap-6 lg:items-end">
              <p className="font-outfit text-base leading-5 text-dark-800/80 lg:text-right">
                {copy.descriptionBefore}
                <strong className="font-semibold text-dark-800">{copy.descriptionHighlight}</strong>
              </p>
              <Link
                aria-label={copy.exploreButtonAriaLabel}
                className="inline-flex h-12 items-center justify-center rounded-[10px] border-[1.5px] border-black/30 bg-white/30 px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors hover:bg-white/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                href={localizePath('/apartments', locale)}
              >
                {copy.exploreButtonLabel}
              </Link>
            </div>
          </div>
        </div>

        <div className={layout.trackClassName} data-location-room-types-carousel>
          <CarouselContent className={layout.contentClassName} role="list">
            {listings.map((listing, index) => (
              <CarouselItem
                key={`${listing.locationId}-${listing.roomType}`}
                className={layout.getSlideClassName(index, listings.length)}
                role="listitem"
              >
                <RoomTypeCard
                  {...getRoomTypeCardProps(listing, locale, gridCopy, {
                    hasPromotion,
                  })}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </div>

        {canScrollCarousel ? (
          <div className={cn(containerClassName, 'pb-10 pt-8 lg:py-8')}>
            <div className="flex items-center justify-end gap-3">
              <CarouselPrevious
                aria-label={copy.carouselPrevious}
                className={carouselControlClassName}
                onClick={handleCarouselPrev}
                size="icon"
                variant="outline"
              />
              <CarouselNext
                aria-label={copy.carouselNext}
                className={carouselControlClassName}
                onClick={handleCarouselNext}
                size="icon"
                variant="outline"
              />
            </div>
          </div>
        ) : (
          <div aria-hidden className="pb-10 lg:pb-16" />
        )}
      </Carousel>
    </section>
  )
}
